import ICart, {
    ICartOrderData,
    ICartProps,
    ICartTotals,
    CartTotalsValidationConfig,
    CartOrderDataValidationConfig
} from './ICart';
import { CartItem } from '../CartItem/CartItem';
import { set, observable, computed } from 'mobx';
import sanitizeData from '~api/utils/sanitizeData';

const cartTotalsDefaults = {
    totalCost: 0,
    formattedTotalCost: '',
    subTotal: 0,
    formattedSubtotal: '',
    isVatApplied: false,
    taxRate: '',
    totalTax: 0,
    formattedTotalTax: '',
    shippingTax: 0,
    formattedShippingTax: '-',
    giftwrapAppliedPrice: 0,
    formattedGiftwrapAppliedPrice: '$0',
    donationPrice: 0,
    hasSalesTax: false,
    discount: {
        isDiscounted: false,
        totalPrice: 0,
        formattedTotalPrice: '',
        discountAmount: 0,
        formattedDiscountAmount: '',
        subtotalWithoutDonation: 0,
        offersSummary: [
            {
                benefit_class: '',
                benefit_value: '',
                offer_name: '',
                discount: ''
            }
        ]
    },
    paymentInstallments: {},
    potentialLoyaltyPoints: 0,
    retailDeliveryFee: 0,
    retailDeliveryFeeJurisdiction: ''
};

export default class Cart implements ICart {
    @observable cartItems: ICart['cartItems'] = [];
    @observable cartOrderData: ICartOrderData = {};
    @observable cartTotals: ICartTotals = cartTotalsDefaults;

    @computed
    public get totalPrice(): ICart['totalPrice'] {
        return this.cartTotals.subTotal;
    }

    @computed
    public get maxTotalItemLimit(): number {
        return this.cartTotals.maxTotalItemLimit || Infinity;
    }

    @computed
    public get totalQty(): ICart['totalQty'] {
        return this.cartItems.reduce(
            (accumQty, item) => accumQty + (Number(item.quantity) || 0),
            0
        );
    }

    @computed
    public get samplesQty(): ICart['samplesQty'] {
        return this.cartItems.reduce(
            (accumQty, item) =>
                item.product.isSample ? accumQty + (item.quantity || 0) : accumQty,
            0
        );
    }

    @computed
    public get pricedSkusQty(): ICart['pricedSkusQty'] {
        return this.totalQty - this.samplesQty;
    }

    @computed
    public get loyaltySkusQty(): ICart['loyaltySkusQty'] {
        return this.cartItems.reduce(
            (accumQty, item) => (item.isLoyaltyReward ? accumQty + (item.quantity || 0) : accumQty),
            0
        );
    }

    @computed
    public get isFull(): boolean {
        return !!(this.maxTotalItemLimit && this.pricedSkusQty > this.maxTotalItemLimit);
    }

    @computed
    public get isCartQtyLimitReached(): boolean {
        return !!(this.maxTotalItemLimit && this.pricedSkusQty === this.maxTotalItemLimit);
    }

    @computed
    public get isTaxDetermined(): boolean {
        return !!(this.cartOrderData.ADDRESS_ID && !this.cartTotals.isVatApplied);
    }

    public static fromJSON(data: ICartProps): Cart {
        const cart = new Cart();
        const newCartItems = data.cartItems.map((cartItem) => CartItem.fromJSON(cartItem));
        // @ts-ignore
        sanitizeData(data.cartTotals, CartTotalsValidationConfig);
        if (data.cartOrderData) {
            // @ts-ignore
            sanitizeData(data.cartOrderData, CartOrderDataValidationConfig);
        }
        set(cart, {
            cartItems: newCartItems || [],
            cartTotals: data.cartTotals,
            cartOrderData: data.cartOrderData
        });

        return cart;
    }
}
